import type { StateCreator } from "zustand";

import { fetchComponentGenomes, fetchSyntenyInfo } from "../../api/fetch.ts";
import { fetchSavedView, postSavedView, putSavedView } from "../../api/saved-view.ts";
import { setupSyntenyWorkerMessageHandler } from "../../main-canvas/messages/handle-alignment-messages.ts";
import { refreshTracks } from "../../main-canvas/messages/post-track-messages.ts";
import { refreshMain } from "../../main-canvas/render-main.ts";
import type { components } from "../../openapi";
import SyntenyWorker from "../../synteny-worker/index.ts?worker";
import { FetchError } from "../../utils/errors.ts";
import type { TrackConfig } from "../../utils/tracks/create-tracks.ts";
import { getSyntenyTypeBySequencingType } from "../../utils/type-conversion.ts";
import type { GenomeViewerStore } from "../store.ts";

export type SavedViewMetadata = Omit<components["schemas"]["CreateOrUpdateSyntenyViewRequest"], "state">;
export type SavedSyntenyView = components["schemas"]["StateRequest"];
export type LoadedSyntenyView = components["schemas"]["StateResponse"];

export interface SaveLoadState {
  viewMetadata: SavedViewMetadata | undefined;
}
export interface SaveLoadActions {
  getView(): SavedSyntenyView;
  setView(view: LoadedSyntenyView, syntenyId: number, onError: (error: Error | FetchError) => void): Promise<void>;
  saveView(metadata: SavedViewMetadata): Promise<number>;
  loadView(viewId: number, syntenyId: number, onError: (error: Error | FetchError) => void): Promise<void>;
}
export type SaveLoadSlice = SaveLoadActions & SaveLoadState;

export const initialSaveLoadState: SaveLoadState = {
  viewMetadata: undefined,
};

export const createSaveLoadSlice: StateCreator<GenomeViewerStore, [], [], SaveLoadSlice> = (set, get) => ({
  ...initialSaveLoadState,
  getView() {
    const {
      trackGroups,
      tracks,
      alignedFamily,
      activeMemberId,
      alignmentDensity,
      pileupRenderingMode,
      basePerPixel,
      scrollX,
      scrollY,
      syntenyOrganism,
    } = get();

    const syntenyView = {
      trackGroups: [...trackGroups.values()].map((group) => ({
        organismId: group.organismId,
        contigId: group.contigId,
        groupId: group.groupId,
        graphics: group.graphics,
        trackIds: group.trackIds,
      })),
      tracks: [...tracks.values()].map((track) => ({
        width: track.width,
        height: track.height,
        text: track.text,
        color: track.color,
        synteny: track.synteny,
        baseStart: track.baseStart!,
        isVisible: track.isVisible,
        trackType: track.trackType,
        trackId: track.trackId,
        groupId: track.groupId,
        subTypeId: track.subType?.id,
        basePerPixel: track.basePerPixel ?? 0,
      })),
      alignedFamily,
      activeMemberId,
      alignmentDensity,
      pileupRenderingMode,
      basePerPixel,
      scrollX,
      scrollY,
      organismId: syntenyOrganism.id!,
    };

    return syntenyView;
  },
  async setView(view, syntenyId, onError) {
    const { tracks, trackGroups, scrollX, scrollY, ...syntenyView } = view;

    try {
      const syntenyInfo = await fetchSyntenyInfo(syntenyId);
      const data = await fetchComponentGenomes(syntenyId);

      const organismMap: GenomeViewerStore["organisms"] = new Map();

      const resurrectedTrackGroups = new Map(
        trackGroups.map(({ contigId, contigSequenceId, organismId, organismName, graphics, groupId, trackIds }) => [
          groupId,
          { organismId, organismName, contigId, contigName: contigSequenceId, groupId, graphics, trackIds },
        ]),
      );

      const storeUpdate: Partial<GenomeViewerStore> = {
        tracks: new Map(), // We populate the tracks later including imageBitmap and worker
        trackGroups: resurrectedTrackGroups,
        scrollX: 0,
        scrollY: 0,
        asynchronousScrolling: false,
        syntenyOrganism: { id: syntenyInfo.id, name: syntenyInfo.name, type: syntenyInfo.sequencingType },
        activeSyntenyType: getSyntenyTypeBySequencingType(syntenyInfo.sequencingType),
      };
      let trackConfigs: TrackConfig[] = [];

      // Process all organisms first
      for (const organism of data) {
        const { id, name, contigs } = organism;

        const contigsMap = new Map(contigs.map((contig) => [contig.id, contig]));

        organismMap.set(id, { id, name, contigs: contigsMap });

        // Store the state updates
        storeUpdate.organisms = organismMap;

        trackConfigs = tracks.map(
          ({ trackType, groupId, subType, color, text, isVisible, trackId }) =>
            ({
              trackType,
              group: groupId,
              options: {
                subType,
                color,
                text,
                isVisible,
                trackId,
              },
            }) as TrackConfig,
        );
      }

      if (!get().syntenyWorker) {
        storeUpdate.syntenyWorker = new SyntenyWorker();
      }

      set({
        ...storeUpdate,
        ...syntenyView,
        positionToNavigate: {
          x: scrollX,
          y: scrollY,
        },
      });
      get().createTracks(trackConfigs, onError);
      refreshTracks();
      refreshMain(); // because of new groups
      setupSyntenyWorkerMessageHandler(set, get);
    } catch (error) {
      if (error instanceof FetchError || error instanceof Error) {
        onError(error);
      }
    }

    refreshTracks();
    refreshMain();
  },
  async saveView(metadata) {
    const syntenyView = get().getView();

    const viewId: number = metadata.id
      ? await putSavedView(metadata, syntenyView) // Edit the view
      : await postSavedView(metadata, syntenyView);

    if (get().viewMetadata) {
      set({ viewMetadata: { ...metadata, id: viewId } });
    }
    return viewId;
  },
  async loadView(viewId, syntenyId, onError) {
    return fetchSavedView(viewId)
      .then((data) => {
        const view: LoadedSyntenyView = data.state;

        if (data.viewType !== "SYNTENY_VIEW") {
          get().loadTracks(syntenyId, [], onError);
          return;
        }

        if (view.organismId != syntenyId) {
          throw new Error("The view cannot be resurrected on this dataset.");
        }

        set({
          viewMetadata: {
            id: data.id,
            name: data.viewName,
            description: data.description,
            projectId: data.projectId,
            itemTagIds: data.itemTags?.map((tag) => tag.id) ?? [],
            path: data.path,
            shared: data.shared,
          },
        });

        return get().setView(view, syntenyId, onError);
      })
      .catch((error) => {
        onError(error);
      });
  },
});
